/**
 * PBI 87080 — catálogo dos tipos de arquivo que o envio manual da Storage aceita.
 *
 * O `kind` é o valor gravado em `storage.type`. Os cinco primeiros são os mesmos
 * que os nodes de importação gravavam (pdf/excel/csv/json/markdown), então o
 * filtro por tipo da listagem e os documentos já enviados seguem iguais; `word`
 * e `powerpoint` são novos. O ícone sai do `kind` (`FileTypeIcon`), não de uma
 * URL gravada no documento.
 */
export type DocumentKind =
  | "pdf"
  | "word"
  | "powerpoint"
  | "excel"
  | "csv"
  | "json"
  | "markdown";

type Signature = "pdf" | "zip" | "zipOrOle" | "text";

interface DocumentFileType {
  extensions: string[];
  mimeTypes: string[];
  signature: Signature;
}

export const DOCUMENT_FILE_TYPES: Record<DocumentKind, DocumentFileType> = {
  pdf: {
    extensions: [".pdf"],
    mimeTypes: ["application/pdf"],
    signature: "pdf",
  },
  word: {
    extensions: [".docx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    signature: "zip",
  },
  powerpoint: {
    extensions: [".pptx"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    signature: "zip",
  },
  excel: {
    extensions: [".xlsx", ".xls"],
    mimeTypes: [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ],
    signature: "zipOrOle",
  },
  csv: {
    extensions: [".csv"],
    mimeTypes: ["text/csv"],
    signature: "text",
  },
  json: {
    extensions: [".json"],
    mimeTypes: ["application/json"],
    signature: "text",
  },
  markdown: {
    extensions: [".md", ".markdown"],
    mimeTypes: ["text/markdown"],
    signature: "text",
  },
};

export const DOCUMENT_KINDS = Object.keys(DOCUMENT_FILE_TYPES) as DocumentKind[];

/** `storage.type` é um dos formatos do catálogo (e portanto tem ícone próprio). */
export function isDocumentKind(type: string | null | undefined): type is DocumentKind {
  // hasOwnProperty, não `in`: `in` acha também "toString" & cia. no protótipo.
  return !!type && Object.prototype.hasOwnProperty.call(DOCUMENT_FILE_TYPES, type);
}

/** Mesmo limite do upload da api (`storage_items_controller`). */
export const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024;

/** Extensões aceitas, para exibir ao usuário (".pdf, .docx, ..."). */
export const ACCEPTED_EXTENSIONS = DOCUMENT_KINDS.flatMap(
  (kind) => DOCUMENT_FILE_TYPES[kind].extensions,
);

/** Formato do `accept` do react-dropzone: filtra o seletor nativo de arquivos. */
export const DROPZONE_ACCEPT: Record<string, string[]> = Object.fromEntries(
  DOCUMENT_KINDS.flatMap((kind) => {
    const { mimeTypes, extensions } = DOCUMENT_FILE_TYPES[kind];
    return mimeTypes.map((mime) => [mime, extensions]);
  }),
);

export function detectDocumentKind(fileName: string): DocumentKind | null {
  const lower = fileName.toLowerCase();
  return (
    DOCUMENT_KINDS.find((kind) =>
      DOCUMENT_FILE_TYPES[kind].extensions.some((ext) => lower.endsWith(ext)),
    ) ?? null
  );
}

export type DocumentRejection = "unsupported" | "tooLarge" | "empty" | "corrupted";

export type DocumentInspection =
  | { ok: true; kind: DocumentKind }
  | { ok: false; reason: DocumentRejection };

// Só o começo do arquivo é lido: o custo é o mesmo para qualquer tamanho.
const HEADER_BYTES = 512;

/**
 * Reconhece o tipo pela extensão e confere se os primeiros bytes batem com ele —
 * pega arquivo corrompido ou renomeado (um .docx que não é ZIP) antes do envio.
 */
export async function inspectDocumentFile(file: File): Promise<DocumentInspection> {
  const kind = detectDocumentKind(file.name);
  if (!kind) return { ok: false, reason: "unsupported" };
  if (file.size > MAX_DOCUMENT_SIZE_BYTES) return { ok: false, reason: "tooLarge" };
  if (file.size === 0) return { ok: false, reason: "empty" };

  let header: Uint8Array;
  try {
    header = await readHeader(file);
  } catch {
    // Arquivo que o navegador não consegue ler é tratado como corrompido.
    return { ok: false, reason: "corrupted" };
  }
  if (!matchesSignature(header, DOCUMENT_FILE_TYPES[kind].signature)) {
    return { ok: false, reason: "corrupted" };
  }
  return { ok: true, kind };
}

function matchesSignature(header: Uint8Array, signature: Signature): boolean {
  const startsWith = (bytes: number[]) => bytes.every((b, i) => header[i] === b);
  const isZip = () => startsWith([0x50, 0x4b, 0x03, 0x04]);
  switch (signature) {
    case "pdf":
      return startsWith([0x25, 0x50, 0x44, 0x46, 0x2d]); // %PDF-
    case "zip":
      return isZip();
    case "zipOrOle":
      return isZip() || startsWith([0xd0, 0xcf, 0x11, 0xe0]);
    case "text":
      // Byte nulo não aparece em texto (UTF-8/ASCII): é binário com extensão errada.
      return !header.includes(0);
  }
}

async function readHeader(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, HEADER_BYTES).arrayBuffer());
}
